import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sheet } from '@/shared-ui/Sheet';
import { Button } from '@/shared-ui/Button';
import { Recorder } from '@/core/services/recorder';
import { submitAudio, isQuotaExceededError } from '@/core/services/noteProcessing';
import { Waveform } from './Waveform';
import { cn } from '@/utils/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  onTaskCreated: (taskId: string) => void;
}

type Mode = 'tap' | 'hold';
type Phase = 'prep' | 'recording' | 'uploading' | 'error';

const MAX_MS = 30 * 60 * 1000;
const HOLD_MIN_MS = 350; // ignore accidental taps in hold mode

export function RecordingSheet({ open, onClose, onTaskCreated }: Props) {
  const navigate = useNavigate();
  const recorderRef = useRef<Recorder | null>(null);
  const holdStartRef = useRef(0);
  const [mode, setMode] = useState<Mode>('tap');
  const [phase, setPhase] = useState<Phase>('prep');
  const [level, setLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      recorderRef.current?.cancel();
      recorderRef.current = null;
      setPhase('prep');
      setLevel(0);
      setElapsedMs(0);
      setError(null);
    }
  }, [open]);

  const startRecording = async (): Promise<void> => {
    setError(null);
    const rec = new Recorder();
    rec.onLevel = setLevel;
    rec.onTick = setElapsedMs;
    try {
      await rec.start();
      recorderRef.current = rec;
      holdStartRef.current = performance.now();
      setPhase('recording');
    } catch (e) {
      setError((e as Error).message ?? '无法访问麦克风');
      setPhase('error');
    }
  };

  const stopAndUpload = async (): Promise<void> => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'recording') return;
    setPhase('uploading');
    try {
      const result = await rec.stop();
      recorderRef.current = null;
      const { taskId } = await submitAudio(result);
      onTaskCreated(taskId);
      onClose();
    } catch (e) {
      if (isQuotaExceededError(e)) {
        onClose();
        navigate('/membership?reason=quota');
        return;
      }
      setError((e as Error).message ?? '上传失败');
      setPhase('error');
    }
  };

  const cancel = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    onClose();
  };

  // ----- Hold-to-record handlers -----

  const onHoldDown = async (e: PointerEvent) => {
    if (mode !== 'hold' || phase !== 'prep') return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    await startRecording();
  };

  const onHoldUp = async (e: PointerEvent) => {
    if (mode !== 'hold' || phase !== 'recording') return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    const elapsed = performance.now() - holdStartRef.current;
    if (elapsed < HOLD_MIN_MS) {
      // accidental tap — discard
      recorderRef.current?.cancel();
      recorderRef.current = null;
      setPhase('prep');
      setElapsedMs(0);
      return;
    }
    await stopAndUpload();
  };

  // ----- Auto-stop at max duration -----

  const seconds = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const atMax = elapsedMs >= MAX_MS;

  useEffect(() => {
    if (atMax && phase === 'recording') void stopAndUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atMax, phase]);

  const recording = phase === 'recording';

  return (
    <Sheet open={open} onClose={cancel}>
      <div className="noise relative flex flex-col items-center gap-5 p-6 pb-8">
        <div className="h-1 w-10 rounded-full bg-border" />

        <div className="flex gap-1 rounded-full bg-bg p-0.5 border border-border">
          {(['tap', 'hold'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => phase === 'prep' && setMode(m)}
              disabled={phase !== 'prep'}
              className={cn(
                'h-7 rounded-full px-3 text-[11px] font-medium transition-colors',
                mode === m ? 'bg-bg-elevated text-fg shadow-sm' : 'text-fg-muted hover:text-fg',
                phase !== 'prep' && 'opacity-50'
              )}
            >
              {m === 'tap' ? '点按' : '按住'}
            </button>
          ))}
        </div>

        <motion.div
          key={phase}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs uppercase tracking-[0.2em] text-fg-muted"
        >
          {phase === 'prep' && (mode === 'hold' ? 'press & hold' : 'ready')}
          {recording && 'recording…'}
          {phase === 'uploading' && 'processing'}
          {phase === 'error' && 'error'}
        </motion.div>

        <div className="relative h-32 w-full">
          <motion.div
            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent/10 via-transparent to-accent-2/10 pointer-events-none"
            animate={{ opacity: recording ? 1 : 0.4 }}
            transition={{ duration: 0.4 }}
          />
          <Waveform level={level} active={recording} className="absolute inset-0 w-full h-full" />
        </div>

        <div className="font-mono text-3xl tabular-nums text-gradient">
          {mm}:{ss}
        </div>

        {error && <div className="text-xs text-danger">{error}</div>}

        {mode === 'hold' && (phase === 'prep' || phase === 'recording') ? (
          <motion.button
            onPointerDown={onHoldDown}
            onPointerUp={onHoldUp}
            onPointerCancel={onHoldUp}
            whileTap={{ scale: 0.94 }}
            animate={recording ? { scale: [1, 1.06, 1] } : { scale: 1 }}
            transition={recording ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' } : undefined}
            className={cn(
              'h-20 w-20 rounded-full glow-accent text-3xl text-accent-contrast select-none touch-none',
              'bg-gradient-to-br from-accent to-accent-2'
            )}
          >
            🎙
          </motion.button>
        ) : (
          <div className="flex gap-3 w-full">
            {phase === 'prep' && mode === 'tap' && (
              <>
                <Button variant="ghost" className="flex-1" onClick={cancel}>取消</Button>
                <Button
                  className="flex-1 glow-accent bg-gradient-to-r from-accent to-accent-2 text-accent-contrast"
                  onClick={() => void startRecording()}
                >
                  开始录音
                </Button>
              </>
            )}
            {recording && mode === 'tap' && (
              <>
                <Button variant="ghost" className="flex-1" onClick={cancel}>取消</Button>
                <Button
                  className="flex-1 breathe bg-gradient-to-r from-accent to-accent-2 text-accent-contrast"
                  onClick={() => void stopAndUpload()}
                >
                  完成
                </Button>
              </>
            )}
            {phase === 'uploading' && <Button loading className="flex-1">处理中…</Button>}
            {phase === 'error' && (
              <>
                <Button variant="ghost" className="flex-1" onClick={cancel}>关闭</Button>
                <Button className="flex-1" onClick={() => void startRecording()}>重试</Button>
              </>
            )}
          </div>
        )}

        {mode === 'hold' && phase === 'prep' && (
          <div className="text-[11px] text-fg-subtle">长按说话，松手提交</div>
        )}
      </div>
    </Sheet>
  );
}
